import React, { useState, useEffect } from 'react';
import { API } from '../App';
import { useT } from '../i18n';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Separator } from './ui/separator';
import ConfirmDialog from './ConfirmDialog';
import XpnsrPageHeader from './XpnsrPageHeader';
import XpnsrEmpty from './XpnsrEmpty';
import { Plus, Trash2, Play, Clock, CalendarClock } from 'lucide-react';

const REPORT_TYPES = ['performance', 'campaigns', 'geo', 'devices', 'funnel', 'cohort', 'audit'];
const CADENCES = ['daily', 'weekly', 'monthly'];
const FORMATS = ['xlsx', 'csv'];
const WEEKDAYS = [['0', 'Mon'], ['1', 'Tue'], ['2', 'Wed'], ['3', 'Thu'], ['4', 'Fri'], ['5', 'Sat'], ['6', 'Sun']];

const EMPTY_FORM = {
  name: '',
  report_type: 'performance',
  cadence: 'daily',
  hour: 9,
  day_of_week: 0,
  day_of_month: 1,
  tz_name: 'UTC',
  format: 'xlsx',
  recipients: '',
};

export default function ScheduledReports() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API + '/scheduled-reports');
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error loading scheduled reports:', err);
      toast.error(t('Error loading scheduled reports'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => setForm(EMPTY_FORM);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error(t('Name is required')); return; }
    try {
      const recipients = form.recipients.split(',').map((x) => x.trim()).filter(Boolean);
      const res = await axios.post(API + '/scheduled-reports', {
        name: form.name.trim(),
        report_type: form.report_type,
        cadence: form.cadence,
        hour: Number(form.hour) || 0,
        day_of_week: Number(form.day_of_week) || 0,
        day_of_month: Number(form.day_of_month) || 1,
        tz_name: form.tz_name || 'UTC',
        format: form.format,
        recipients: recipients,
      });
      setItems((prev) => [res.data, ...prev]);
      setCreateOpen(false);
      resetForm();
      toast.success(t('Schedule created'));
    } catch (err) {
      console.error('Error creating schedule:', err);
      toast.error(t('Error creating schedule'));
    }
  };

  const toggleEnabled = async (item) => {
    try {
      const res = await axios.patch(API + '/scheduled-reports/' + item.id, { enabled: !item.enabled });
      setItems((prev) => prev.map((x) => (x.id === item.id ? res.data : x)));
    } catch (err) {
      toast.error(t('Failed to update schedule'));
    }
  };

  const runNow = async (item) => {
    try {
      await axios.post(API + '/scheduled-reports/' + item.id + '/run-now');
      toast.success(t('Report queued'));
      fetchItems();
    } catch (err) {
      toast.error(t('Failed to run report'));
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;
    try {
      await axios.delete(API + '/scheduled-reports/' + id);
      setItems((prev) => prev.filter((x) => x.id !== id));
      setDeleteTarget(null);
      toast.success(t('Schedule deleted'));
    } catch (err) {
      toast.error(t('Error deleting schedule'));
    }
  };

  const cadenceLabel = (item) => {
    if (item.cadence === 'weekly') {
      const wd = WEEKDAYS[item.day_of_week] ? WEEKDAYS[item.day_of_week][1] : '';
      return t('Weekly') + ' · ' + wd + ' · ' + String(item.hour).padStart(2, '0') + ':00';
    }
    if (item.cadence === 'monthly') {
      return t('Monthly') + ' · ' + t('day') + ' ' + item.day_of_month + ' · ' + String(item.hour).padStart(2, '0') + ':00';
    }
    return t('Daily') + ' · ' + String(item.hour).padStart(2, '0') + ':00';
  };

  const renderForm = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('Name')}</Label>
          <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder={t('e.g. Weekly digest')} />
        </div>
        <div className="space-y-2">
          <Label>{t('Report type')}</Label>
          <Select value={form.report_type} onValueChange={(v) => setForm((p) => ({ ...p, report_type: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{REPORT_TYPES.map((v) => (<SelectItem key={v} value={v}>{v}</SelectItem>))}</SelectContent>
          </Select>
        </div>
      </div>
      <Separator />
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('Cadence')}</Label>
          <Select value={form.cadence} onValueChange={(v) => setForm((p) => ({ ...p, cadence: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{CADENCES.map((v) => (<SelectItem key={v} value={v}>{t(v)}</SelectItem>))}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{t('Hour (0-23)')}</Label>
          <Input type="number" min={0} max={23} value={form.hour} onChange={(e) => setForm((p) => ({ ...p, hour: e.target.value }))} />
        </div>
      </div>
      {form.cadence === 'weekly' ? (
        <div className="space-y-2">
          <Label>{t('Day of week')}</Label>
          <Select value={String(form.day_of_week)} onValueChange={(v) => setForm((p) => ({ ...p, day_of_week: Number(v) }))}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>{WEEKDAYS.map(([v, l]) => (<SelectItem key={v} value={v}>{t(l)}</SelectItem>))}</SelectContent>
          </Select>
        </div>
      ) : null}
      {form.cadence === 'monthly' ? (
        <div className="space-y-2">
          <Label>{t('Day of month (1-31)')}</Label>
          <Input type="number" min={1} max={31} value={form.day_of_month} onChange={(e) => setForm((p) => ({ ...p, day_of_month: e.target.value }))} className="w-40" />
        </div>
      ) : null}
      <Separator />
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('Format')}</Label>
          <Select value={form.format} onValueChange={(v) => setForm((p) => ({ ...p, format: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{FORMATS.map((v) => (<SelectItem key={v} value={v}>{v.toUpperCase()}</SelectItem>))}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{t('Timezone')}</Label>
          <Input value={form.tz_name} onChange={(e) => setForm((p) => ({ ...p, tz_name: e.target.value }))} placeholder="UTC" />
        </div>
      </div>
      <div className="space-y-2">
        <Label>{t('Recipients')}</Label>
        <Input value={form.recipients} onChange={(e) => setForm((p) => ({ ...p, recipients: e.target.value }))} placeholder="ops@example.com, boss@example.com" />
        <p className="text-xs text-muted-foreground">{t('Comma-separated email addresses.')}</p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-8">
        <div className="skeleton h-8 w-48 mb-6"></div>
        <div className="skeleton h-96 w-full"></div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      <XpnsrPageHeader
        kicker={t('Analytics')}
        title={t('Scheduled reports')}
        subtitle={t('Recurring reports emailed to recipients on a daily/weekly/monthly cadence.')}
        actions={
          <Dialog open={createOpen} onOpenChange={(open) => { setCreateOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}><Plus className="w-4 h-4 mr-2" />{t('New schedule')}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t('New scheduled report')}</DialogTitle>
                <DialogDescription>{t('Pick a report, cadence and recipients.')}</DialogDescription>
              </DialogHeader>
              {renderForm()}
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setCreateOpen(false)}>{t('Cancel')}</Button>
                <Button onClick={handleCreate}>{t('Create')}</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />

      {items.length === 0 ? (
        <Card>
          <CardContent className="py-10">
            <XpnsrEmpty icon={CalendarClock} title={t('No scheduled reports yet')} subtitle={t('Create one to receive recurring reports by email.')} />
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((item) => (
            <Card key={item.id}>
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <CalendarClock className="w-5 h-5 text-primary shrink-0" />
                    <CardTitle className="truncate">{item.name}</CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={item.enabled !== false} onCheckedChange={() => toggleEnabled(item)} />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary" className="text-xs">{item.report_type}</Badge>
                  <Badge variant="outline" className="text-xs">{item.format ? item.format.toUpperCase() : 'XLSX'}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="w-4 h-4" />
                  <span>{cadenceLabel(item)} ({item.tz_name || 'UTC'})</span>
                </div>
                {(item.recipients || []).length > 0 ? (
                  <p className="text-xs text-muted-foreground truncate">{t('To')}: {item.recipients.join(', ')}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">{t('No recipients set')}</p>
                )}
                {item.last_run_at ? (
                  <p className="text-xs text-muted-foreground">{t('Last run')}: {new Date(item.last_run_at).toLocaleString()} · {item.last_run_status || ''}</p>
                ) : null}
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button variant="outline" size="sm" onClick={() => runNow(item)}><Play className="w-4 h-4 mr-1" />{t('Run now')}</Button>
                  <Button variant="ghost" size="sm" aria-label={t('Delete schedule')} title={t('Delete schedule')} onClick={() => setDeleteTarget(item)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title={t('Delete schedule')}
        description={t('This permanently deletes the scheduled report.')}
        confirmLabel={t('Delete')}
        onConfirm={() => handleDelete(deleteTarget && deleteTarget.id)}
      />
    </div>
  );
}
